import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Heart, Apple, CreditCard, Loader2, ChevronDown, ChevronUp, PawPrint } from "lucide-react";

const ONE_TIME = ["5", "10", "20", "50", "100"];
const MONTHLY = ["5", "10", "20", "50", "100"];

/**
 * Stripe Apple-Pay-enabled donation card with optional memorial dedication.
 *
 * Flow:
 *  - User picks one_time | monthly + package + optional dedication
 *  - Frontend POSTs to /api/donations/checkout (amount enforced server-side)
 *  - Backend creates Stripe Checkout session, returns URL
 *  - Browser redirects to Stripe Checkout (Apple Pay surfaces natively on iOS)
 *  - After payment, Stripe redirects to /donate/thanks?session_id=... which polls status
 */
export default function StripeDonateCard() {
  const [mode, setMode] = useState("one_time");
  const [pkg, setPkg] = useState("10");
  const [custom, setCustom] = useState("");
  const [dedicationOpen, setDedicationOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ded, setDed] = useState({
    pet_name: "",
    donor_name: "",
    donor_email: "",
    message: "",
    show_publicly: true,
  });

  const isCustom = pkg === "custom";

  const submit = async () => {
    if (mode === "monthly" && pkg === "custom") {
      toast.error("Monthly donations must use a preset amount.");
      return;
    }
    if (isCustom && (!custom || Number(custom) < 1)) {
      toast.error("Enter a custom amount of at least $1.");
      return;
    }
    setSubmitting(true);
    try {
      const dedicationPayload =
        ded.pet_name || ded.message
          ? {
              pet_name: ded.pet_name.trim() || null,
              donor_name: ded.donor_name.trim() || null,
              donor_email: ded.donor_email.trim() || null,
              message: ded.message.trim() || null,
              show_publicly: !!ded.show_publicly,
            }
          : null;

      const body = {
        mode,
        package: pkg,
        custom_amount: isCustom ? Number(custom) : null,
        origin_url: window.location.origin,
        dedication: dedicationPayload,
      };
      const r = await api.post("/donations/checkout", body);
      if (r.data?.url) {
        // Send the user to Stripe Checkout (Apple Pay shows automatically on iOS Safari)
        window.location.href = r.data.url;
      } else {
        toast.error("Could not start checkout. Try again.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Checkout failed. Try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border-2 border-zinc-900 bg-zinc-900 text-white" data-testid="stripe-donate-card">
      <div className="bg-red-600 text-white px-5 py-3 border-b-2 border-zinc-900">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Heart size={14} className="fill-white flex-shrink-0" />
            <span className="label-eyebrow truncate text-white">Support Paws on Record</span>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest font-bold whitespace-nowrap">
            Apple Pay · Card
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="font-display font-black text-2xl tracking-tight leading-tight">
          Keep the registry free.
        </div>
        <p className="text-sm text-zinc-300 mt-2 leading-relaxed">
          One tap with Apple Pay. $5 funds a day of hosting. Pick one-time or monthly.
        </p>

        {/* Mode toggle */}
        <div className="mt-5 grid grid-cols-2 border border-zinc-700" role="tablist">
          {[
            { k: "one_time", label: "One-time" },
            { k: "monthly", label: "Monthly · Sponsor" },
          ].map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => {
                setMode(t.k);
                if (t.k === "monthly" && pkg === "custom") setPkg("10");
              }}
              className={`py-2.5 text-xs font-bold uppercase tracking-widest transition-colors ${
                mode === t.k
                  ? "bg-white text-zinc-900"
                  : "bg-transparent text-zinc-400 hover:text-white"
              }`}
              data-testid={`donate-mode-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Amount picker */}
        <div className="mt-4 grid grid-cols-5 gap-1.5">
          {(mode === "one_time" ? ONE_TIME : MONTHLY).map((amt) => (
            <button
              key={amt}
              type="button"
              onClick={() => setPkg(amt)}
              className={`py-3 px-1 border text-center font-bold transition-colors ${
                pkg === amt
                  ? "border-red-500 bg-red-600 text-white"
                  : "border-zinc-700 bg-zinc-800/60 text-zinc-200 hover:border-red-500 hover:text-white"
              }`}
              data-testid={`donate-amount-${amt}`}
            >
              <div className="text-lg sm:text-xl">${amt}</div>
              <div className="text-[9px] uppercase tracking-widest mt-0.5 opacity-80">
                {mode === "monthly" ? "/ mo" : "one-time"}
              </div>
            </button>
          ))}
        </div>

        {mode === "one_time" && (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setPkg("custom")}
              className={`w-full py-2.5 border text-xs font-bold uppercase tracking-widest transition-colors ${
                pkg === "custom"
                  ? "border-red-500 bg-red-600/20 text-white"
                  : "border-zinc-700 text-zinc-300 hover:border-red-500 hover:text-white"
              }`}
              data-testid="donate-amount-custom-btn"
            >
              Custom amount
            </button>
            {isCustom && (
              <div className="mt-2 flex items-center gap-2 border border-red-500 bg-zinc-800/60 px-3 py-2">
                <span className="text-white font-mono">$</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  className="flex-1 bg-transparent outline-none text-white text-base font-bold"
                  placeholder="100"
                  data-testid="donate-amount-custom-input"
                />
                <span className="text-[11px] uppercase tracking-widest text-zinc-400">USD</span>
              </div>
            )}
          </div>
        )}

        {/* Dedication toggle */}
        <button
          type="button"
          onClick={() => setDedicationOpen((v) => !v)}
          className="mt-5 w-full border border-zinc-700 bg-zinc-800/40 px-3 py-2.5 text-xs uppercase tracking-widest font-bold flex items-center justify-between hover:border-red-500 transition-colors"
          data-testid="donate-dedication-toggle"
        >
          <span className="flex items-center gap-2">
            <PawPrint size={12} className="text-red-500" />
            Donate in memory of an animal (optional)
          </span>
          {dedicationOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {dedicationOpen && (
          <div className="mt-3 border border-zinc-700 bg-zinc-800/40 p-4 space-y-3" data-testid="donate-dedication-form">
            <div>
              <label className="label-eyebrow text-zinc-400 block mb-1.5">Pet&apos;s name</label>
              <input
                value={ded.pet_name}
                onChange={(e) => setDed({ ...ded, pet_name: e.target.value })}
                placeholder="Bear, Daisy, Sasha..."
                maxLength={80}
                className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-red-500"
                data-testid="dedication-pet-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow text-zinc-400 block mb-1.5">Your first name</label>
                <input
                  value={ded.donor_name}
                  onChange={(e) => setDed({ ...ded, donor_name: e.target.value })}
                  placeholder="Optional"
                  maxLength={80}
                  className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-red-500"
                  data-testid="dedication-donor-name"
                />
              </div>
              <div>
                <label className="label-eyebrow text-zinc-400 block mb-1.5">Email (for receipt)</label>
                <input
                  type="email"
                  value={ded.donor_email}
                  onChange={(e) => setDed({ ...ded, donor_email: e.target.value })}
                  placeholder="you@email.com"
                  maxLength={120}
                  className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-red-500"
                  data-testid="dedication-donor-email"
                />
              </div>
            </div>
            <div>
              <label className="label-eyebrow text-zinc-400 block mb-1.5">
                Message (optional, up to 400 chars)
              </label>
              <textarea
                value={ded.message}
                onChange={(e) => setDed({ ...ded, message: e.target.value })}
                rows={3}
                maxLength={400}
                placeholder='"You were rescued. You were loved. We fight for the next one."'
                className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm outline-none focus:border-red-500 leading-relaxed"
                data-testid="dedication-message"
              />
              <div className="text-[10px] font-mono text-zinc-500 mt-1 text-right">
                {ded.message.length}/400
              </div>
            </div>
            <label className="flex items-start gap-2 cursor-pointer" data-testid="dedication-show-publicly-label">
              <input
                type="checkbox"
                checked={ded.show_publicly}
                onChange={(e) => setDed({ ...ded, show_publicly: e.target.checked })}
                className="mt-0.5 accent-red-600"
                data-testid="dedication-show-publicly"
              />
              <span className="text-xs text-zinc-300 leading-relaxed">
                Show this dedication on our public memorial wall at{" "}
                <span className="font-mono">/in-memory</span>. Uncheck to keep it
                private (admin-only).
              </span>
            </label>
          </div>
        )}

        {/* Submit */}
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="mt-5 w-full bg-white text-zinc-900 hover:bg-red-600 hover:text-white border-2 border-white hover:border-red-600 font-bold uppercase tracking-widest text-sm py-4 flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
          data-testid="donate-submit"
        >
          {submitting ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <>
              <Apple size={16} /> <CreditCard size={14} />
            </>
          )}
          {submitting
            ? "Opening secure checkout..."
            : mode === "monthly"
              ? "Sponsor monthly →"
              : "Donate now →"}
        </button>

        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-3 text-center">
          Secured by Stripe · You&apos;ll see the dedication on the next page
        </div>
      </div>
    </div>
  );
}
